<!-- Campaña: convive con el sidebar del CRM (la ruta está dentro del
     AppLayoutComponent en app.routes.ts). El editor de contenido GrapesJS
     se abre en un modal fullscreen aparte (ver al final del archivo). -->
<div class="flex flex-col">

<!-- Top bar sticky con back / nombre / status / acciones -->
<header class="sticky top-0 z-30 flex items-center gap-3 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-4 py-2.5 -mx-4 md:-mx-6">
  <a routerLink="/marketing-email"
     class="inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
     title="Volver a Correo de marketing">
    <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
    </svg>
    <span class="hidden sm:inline">Correos</span>
  </a>
  <span class="text-gray-300 dark:text-gray-700">/</span>
  <span class="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded bg-blue-100 text-xs text-blue-700 dark:bg-blue-900/40 dark:text-blue-400">
    📨
  </span>
  <div class="min-w-0 flex items-center gap-2 flex-1">
    <span class="text-sm font-semibold text-gray-900 dark:text-white truncate">
      {{ form.name || (isEditing ? 'Correo' : 'Nuevo correo') }}
    </span>
    @if (campaign) {
      <span class="rounded-full bg-gray-100 dark:bg-gray-800 px-2 py-0.5 text-[10px] font-medium text-gray-700 dark:text-gray-300">
        {{ statusLabel(campaign.status) }}
      </span>
      @if (isReadOnly()) {
        <span class="text-[10px] text-amber-600 dark:text-amber-300">(solo lectura)</span>
      }
    }
    @if (campaign?.status === 'DRAFT' && campaign?.lastError) {
      <!-- Programado que volvió a borrador (p. ej. sus segmentos pasaron a borrador): el motivo. -->
      <span class="text-xs text-amber-600 dark:text-amber-400 truncate" [title]="campaign?.lastError ?? ''">{{ campaign?.lastError }}</span>
    }
    @if (successMessage) {
      <span class="text-xs text-green-600 dark:text-green-400 truncate">{{ successMessage }}</span>
    }
    @if (error) {
      <span class="text-xs text-red-600 dark:text-red-400 truncate">{{ error }}</span>
    }
  </div>

  @if (!isReadOnly() && !loading) {
    <div class="flex items-center gap-2">
      <button *appHasPermission="'campaigns:edit'" type="button" (click)="saveDraft()" [disabled]="guardarDeshabilitado"
              class="hidden md:inline-flex items-center gap-1.5 rounded-full border border-gray-300 dark:border-gray-700 px-4 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-60">
        <i class="fas fa-save"></i> Guardar borrador
      </button>
      <!-- Inc 42: con una fecha de programación futura válida, "Programar" es la
           acción primaria y "Lanzar correo" queda deshabilitado (pulsarlo enviaba
           al instante el correo que el usuario acababa de programar). -->
      <button *appHasPermission="'campaigns:edit'" type="button" (click)="schedule()" [disabled]="guardarDeshabilitado || !form.scheduledAt || isScheduledDateInPast"
              class="hidden md:inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-medium disabled:opacity-60"
              [class.bg-brand-500]="hasFutureSchedule"
              [class.text-white]="hasFutureSchedule"
              [class.hover:bg-brand-600]="hasFutureSchedule"
              [class.font-semibold]="hasFutureSchedule"
              [class.border]="!hasFutureSchedule"
              [class.border-blue-300]="!hasFutureSchedule"
              [class.dark:border-blue-700]="!hasFutureSchedule"
              [class.bg-blue-50]="!hasFutureSchedule"
              [class.dark:bg-blue-900\/20]="!hasFutureSchedule"
              [class.text-blue-700]="!hasFutureSchedule"
              [class.dark:text-blue-300]="!hasFutureSchedule"
              [class.hover:bg-blue-100]="!hasFutureSchedule">
        <i class="fas fa-clock"></i> Programar
      </button>
      <button *appHasPermission="'campaigns:edit'" type="button" (click)="launchNow()"
              [disabled]="saving || hasFutureSchedule"
              [title]="lanzarAhoraTitle"
              class="inline-flex items-center gap-1.5 rounded-full px-5 py-2 text-xs disabled:opacity-60 disabled:cursor-not-allowed"
              [class.bg-brand-500]="!hasFutureSchedule"
              [class.dark:bg-white]="!hasFutureSchedule"
              [class.text-white]="!hasFutureSchedule"
              [class.dark:text-gray-900]="!hasFutureSchedule"
              [class.font-semibold]="!hasFutureSchedule"
              [class.hover:bg-brand-600]="!hasFutureSchedule"
              [class.dark:hover:bg-gray-100]="!hasFutureSchedule"
              [class.border]="hasFutureSchedule"
              [class.border-gray-300]="hasFutureSchedule"
              [class.dark:border-gray-700]="hasFutureSchedule"
              [class.text-gray-700]="hasFutureSchedule"
              [class.dark:text-gray-300]="hasFutureSchedule"
              [class.font-medium]="hasFutureSchedule">
        <i class="fas fa-paper-plane"></i> Lanzar correo
      </button>
    </div>
  }
</header>

<div class="pt-4">

<!-- Aviso prominente: hay que guardar como borrador antes de poder agregar
     destinatarios o editar el contenido del email. Visible solo en campañas
     nuevas (sin id). Se autoresuelve al click "Guardar borrador". -->
@if (!isEditing && !loading) {
  <div class="mb-4 rounded-2xl border-2 border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-4">
    <div class="flex items-start gap-3">
      <div class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-amber-200 dark:bg-amber-800 text-amber-700 dark:text-amber-200">
        <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
        </svg>
      </div>
      <div class="flex-1 min-w-0">
        <p class="text-sm font-semibold text-amber-900 dark:text-amber-100">
          Guardá como borrador para continuar
        </p>
        <p class="mt-0.5 text-xs text-amber-800 dark:text-amber-200">
          Necesitás guardar el correo antes de agregar destinatarios (segmentos, contactos, exclusiones) y editar el contenido del correo.
          Mínimo para el borrador: el nombre del correo.
        </p>
      </div>
      <button type="button" (click)="saveDraft()" [disabled]="guardarDeshabilitado"
              class="flex-shrink-0 inline-flex items-center gap-1.5 rounded-full bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 text-xs font-semibold disabled:opacity-60">
        <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
        </svg>
        {{ saving ? 'Guardando…' : 'Guardar borrador' }}
      </button>
    </div>
  </div>
}

@if (loading) {
  <div class="space-y-3">
    @for (i of [1,2,3,4]; track i) {
      <div class="h-32 animate-pulse rounded-2xl bg-gray-100 dark:bg-gray-800"></div>
    }
  </div>
} @else {
  <!-- Two-column layout: config (izquierda) + preview del contenido (derecha).
       La edición del HTML del email NO está embebida acá — abre un modal
       fullscreen vía botón "Editar contenido". -->
  <div class="flex flex-col items-start gap-6 lg:flex-row">

    <!-- ── COLUMNA IZQUIERDA: configuración ── -->
    <div class="w-full space-y-4 lg:flex-1 lg:min-w-[320px]">

      <!-- Destinatarios — bloque unificado -->
      <section class="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-visible">
        <header class="flex items-center justify-between gap-2 border-b border-gray-100 dark:border-gray-800 px-5 py-3.5">
          <h2 class="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white/90">
            <i class="fas fa-users-viewfinder text-blue-500"></i>
            Destinatarios
          </h2>
          @if (previewLoading) {
            <span class="text-xs text-gray-400">Calculando…</span>
          } @else if (preview) {
            <span class="text-xs text-gray-500 dark:text-gray-400">
              <strong class="text-gray-900 dark:text-white/90">{{ preview.finalEstimate }}</strong>
              · {{ preview.included }} incluidos − {{ preview.excluded }} excluidos
            </span>
          } @else {
            <span class="text-xs text-gray-400">~{{ totalEstimatedContacts }} contactos</span>
          }
        </header>
        <div class="px-5 py-4 space-y-5">

          @if (!isEditing) {
            <div class="rounded-xl border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-3">
              <p class="text-xs text-amber-800 dark:text-amber-200 font-medium">
                <i class="fas fa-lock"></i>
                Bloqueado hasta guardar como borrador
              </p>
              <p class="mt-1 text-[11px] text-amber-700 dark:text-amber-300">
                Una vez guardado el correo podrás adjuntar segmentos, contactos individuales y configurar exclusiones.
              </p>
              <button type="button" (click)="saveDraft()" [disabled]="saving"
                      class="mt-2 inline-flex items-center gap-1.5 rounded-full bg-amber-600 hover:bg-amber-700 text-white px-3 py-1 text-xs font-medium disabled:opacity-60">
                {{ saving ? 'Guardando…' : 'Guardar borrador' }}
              </button>
            </div>
          } @else {

            <!-- Bloque 1: segmentos incluidos -->
            <div>
              <p class="mb-2 text-[0.7rem] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Segmentos incluidos
              </p>
              <div class="space-y-2">
                @for (l of campaignLists; track l.id) {
                  <div class="flex items-center justify-between rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 px-3 py-2.5">
                    <div class="min-w-0 flex-1">
                      <p class="text-sm font-medium text-gray-900 dark:text-white/90 truncate">{{ segmentName(l.idContactSegment) }}</p>
                      <p class="text-xs text-gray-500 dark:text-gray-400">
                        {{ l.sourceType === 'STATIC' ? 'Estática' : 'Dinámica' }} · {{ segmentTotal(l.idContactSegment) }} contactos
                      </p>
                      @if (inactiveSegmentLabel(l.idContactSegment); as estado) {
                        <p class="mt-0.5 text-xs font-medium text-amber-600 dark:text-amber-400">{{ estado }} · no se enviará</p>
                      }
                    </div>
                    <div class="ml-2 flex items-center gap-1">
                      <button type="button" (click)="openSegmentMembers(l.idContactSegment)"
                              class="inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-medium text-blue-600 hover:bg-blue-50 dark:text-blue-300 dark:hover:bg-blue-900/20"
                              title="Ver contactos">
                        <i class="fas fa-eye"></i> Ver contactos
                      </button>
                      @if (!isReadOnly()) {
                        <button type="button" (click)="detachSegment(l)"
                                class="rounded-full p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20">
                          <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      }
                    </div>
                  </div>
                }
                @if (campaignLists.length === 0) {
                  <p class="rounded-xl border border-dashed border-gray-300 dark:border-gray-700 px-3 py-3 text-center text-xs text-gray-400">
                    Aún no hay segmentos vinculados.
                  </p>
                }
              </div>

              @if (!isReadOnly()) {
                <div class="relative mt-3">
                  <button type="button" (click)="toggleSegmentSelector()"
                          class="inline-flex items-center gap-2 rounded-full border border-dashed border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20 px-4 py-1.5 text-xs font-medium text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/40">
                    <i class="fas fa-plus"></i> Adjuntar segmento
                  </button>
                  @if (segmentSelectorOpen) {
                    <div class="absolute left-0 top-full z-30 mt-2 w-full max-w-sm rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-xl">
                      <div class="border-b border-gray-100 dark:border-gray-800 p-2">
                        <input type="text" [(ngModel)]="segmentSearchTerm" placeholder="🔍 Buscar segmento…"
                               class="w-full rounded-full border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-3 py-1.5 text-xs text-gray-700 dark:text-gray-300" />
                      </div>
                      <div class="max-h-60 overflow-y-auto py-1">
                        @for (s of availableSegments; track s.id) {
                          <button type="button" (click)="attachSegment(s)"
                                  class="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800">
                            <div class="min-w-0 flex-1">
                              <p class="truncate text-gray-900 dark:text-white/90">{{ s.name }}</p>
                              <p class="text-xs text-gray-500 dark:text-gray-400">
                                {{ s.type === 'DYNAMIC' ? 'Dinámica' : 'Estática' }} · {{ s.totalContacts }} contactos
                              </p>
                            </div>
                          </button>
                        }
                        @if (availableSegments.length === 0) {
                          <p class="px-3 py-3 text-center text-xs text-gray-400">No hay segmentos disponibles</p>
                        }
                      </div>
                    </div>
                  }
                </div>
              }
            </div>

            <!-- Bloque 2: contactos individuales -->
            <div class="border-t border-gray-100 dark:border-gray-800 pt-4">
              <p class="mb-2 text-[0.7rem] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Contactos individuales ({{ manualRecipients.length }})
              </p>
              <div class="space-y-2">
                @for (r of manualRecipients; track r.id) {
                  <div class="flex items-center justify-between rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 px-3 py-2.5">
                    <div class="min-w-0 flex-1">
                      <p class="text-sm font-medium text-gray-900 dark:text-white/90 truncate">{{ r.fullName || '—' }}</p>
                      <p class="text-xs text-gray-500 dark:text-gray-400 truncate">{{ r.email }}</p>
                    </div>
                    @if (!isReadOnly()) {
                      <button type="button" (click)="removeManualRecipient(r)"
                              class="ml-2 rounded-full p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20">
                        <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    }
                  </div>
                }
                @if (manualRecipients.length === 0) {
                  <p class="rounded-xl border border-dashed border-gray-300 dark:border-gray-700 px-3 py-3 text-center text-xs text-gray-400">
                    Sin contactos individuales adjuntos.
                  </p>
                }
              </div>
              @if (!isReadOnly()) {
                <button type="button" (click)="openContactPicker('INCLUDE')"
                        class="mt-3 inline-flex items-center gap-2 rounded-full border border-dashed border-teal-300 dark:border-teal-700 bg-teal-50 dark:bg-teal-900/20 px-4 py-1.5 text-xs font-medium text-teal-700 dark:text-teal-300 hover:bg-teal-100 dark:hover:bg-teal-900/40">
                  <i class="fas fa-plus"></i> Agregar contactos
                </button>
              }
            </div>

            <!-- Bloque 3: exclusiones -->
            <div class="border-t border-gray-100 dark:border-gray-800 pt-4">
              <div class="mb-2 flex items-center justify-between">
                <p class="text-[0.7rem] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Excluir del envío
                </p>
                <div class="inline-flex rounded-full border border-gray-200 dark:border-gray-700 text-[0.65rem] overflow-hidden">
                  <button type="button" (click)="excludeTab = 'CONTACTS'"
                          [class.bg-red-50]="excludeTab === 'CONTACTS'"
                          [class.text-red-700]="excludeTab === 'CONTACTS'"
                          [class.dark:bg-red-900/30]="excludeTab === 'CONTACTS'"
                          [class.dark:text-red-300]="excludeTab === 'CONTACTS'"
                          [class.text-gray-500]="excludeTab !== 'CONTACTS'"
                          class="px-2.5 py-1">
                    Contactos ({{ excludedContacts.length }})
                  </button>
                  <button type="button" (click)="excludeTab = 'SEGMENTS'"
                          [class.bg-red-50]="excludeTab === 'SEGMENTS'"
                          [class.text-red-700]="excludeTab === 'SEGMENTS'"
                          [class.dark:bg-red-900/30]="excludeTab === 'SEGMENTS'"
                          [class.dark:text-red-300]="excludeTab === 'SEGMENTS'"
                          [class.text-gray-500]="excludeTab !== 'SEGMENTS'"
                          class="px-2.5 py-1">
                    Segmentos ({{ excludedSegments.length }})
                  </button>
                </div>
              </div>

              @if (excludeTab === 'CONTACTS') {
                <div class="space-y-2">
                  @for (c of excludedContacts; track c.id) {
                    <div class="flex items-center justify-between rounded-xl border border-red-100 dark:border-red-900/40 bg-red-50/40 dark:bg-red-900/10 px-3 py-2.5">
                      <div class="min-w-0 flex-1">
                        <p class="text-sm font-medium text-gray-900 dark:text-white/90 truncate">{{ c.fullName || '—' }}</p>
                        <p class="text-xs text-gray-500 dark:text-gray-400 truncate">{{ c.email }}</p>
                      </div>
                      @if (!isReadOnly()) {
                        <button type="button" (click)="removeExcludedContact(c)"
                                class="ml-2 rounded-full p-1.5 text-gray-400 hover:bg-red-100 hover:text-red-700 dark:hover:bg-red-900/40">
                          <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      }
                    </div>
                  }
                  @if (excludedContacts.length === 0) {
                    <p class="rounded-xl border border-dashed border-gray-300 dark:border-gray-700 px-3 py-3 text-center text-xs text-gray-400">
                      Sin contactos excluidos.
                    </p>
                  }
                </div>
                @if (!isReadOnly()) {
                  <button type="button" (click)="openContactPicker('EXCLUDE')"
                          class="mt-3 inline-flex items-center gap-2 rounded-full border border-dashed border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 px-4 py-1.5 text-xs font-medium text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/40">
                    <i class="fas fa-user-slash"></i> Excluir contactos
                  </button>
                }
              } @else {
                <div class="space-y-2">
                  @for (e of excludedSegments; track e.id) {
                    <div class="flex items-center justify-between rounded-xl border border-red-100 dark:border-red-900/40 bg-red-50/40 dark:bg-red-900/10 px-3 py-2.5">
                      <div class="min-w-0 flex-1">
                        <p class="text-sm font-medium text-gray-900 dark:text-white/90 truncate">{{ segmentName(e.idContactSegment) }}</p>
                        <p class="text-xs text-gray-500 dark:text-gray-400">
                          {{ e.sourceType === 'STATIC' ? 'Estática' : 'Dinámica' }} · {{ segmentTotal(e.idContactSegment) }} contactos
                        </p>
                        @if (inactiveSegmentLabel(e.idContactSegment); as estado) {
                          <p class="mt-0.5 text-xs font-medium text-amber-600 dark:text-amber-400">{{ estado }} · sus contactos se siguen excluyendo</p>
                        }
                      </div>
                      @if (!isReadOnly()) {
                        <button type="button" (click)="detachExcludedSegment(e)"
                                class="ml-2 rounded-full p-1.5 text-gray-400 hover:bg-red-100 hover:text-red-700 dark:hover:bg-red-900/40">
                          <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      }
                    </div>
                  }
                  @if (excludedSegments.length === 0) {
                    <p class="rounded-xl border border-dashed border-gray-300 dark:border-gray-700 px-3 py-3 text-center text-xs text-gray-400">
                      Sin segmentos excluidos.
                    </p>
                  }
                </div>
                @if (!isReadOnly()) {
                  <div class="relative mt-3">
                    <button type="button" (click)="toggleExcludedSegmentSelector()"
                            class="inline-flex items-center gap-2 rounded-full border border-dashed border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 px-4 py-1.5 text-xs font-medium text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/40">
                      <i class="fas fa-ban"></i> Excluir segmento
                    </button>
                    @if (excludedSegmentSelectorOpen) {
                      <div class="absolute left-0 top-full z-30 mt-2 w-full max-w-sm rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-xl">
                        <div class="border-b border-gray-100 dark:border-gray-800 p-2">
                          <input type="text" [(ngModel)]="excludedSegmentSearchTerm" placeholder="🔍 Buscar segmento…"
                                 class="w-full rounded-full border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-3 py-1.5 text-xs text-gray-700 dark:text-gray-300" />
                        </div>
                        <div class="max-h-60 overflow-y-auto py-1">
                          @for (s of availableExcludedSegments; track s.id) {
                            <button type="button" (click)="attachExcludedSegment(s)"
                                    class="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800">
                              <div class="min-w-0 flex-1">
                                <p class="truncate text-gray-900 dark:text-white/90">{{ s.name }}</p>
                                <p class="text-xs text-gray-500 dark:text-gray-400">
                                  {{ s.type === 'DYNAMIC' ? 'Dinámica' : 'Estática' }} · {{ s.totalContacts }} contactos
                                </p>
                              </div>
                            </button>
                          }
                          @if (availableExcludedSegments.length === 0) {
                            <p class="px-3 py-3 text-center text-xs text-gray-400">No hay segmentos disponibles</p>
                          }
                        </div>
                      </div>
                    }
                  </div>
                }
              }
            </div>

          }
        </div>
      </section>

      <!-- Programación -->
      <section class="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
        <header class="flex items-center gap-2 border-b border-gray-100 dark:border-gray-800 px-5 py-3.5">
          <h2 class="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white/90">
            <i class="fas fa-calendar-alt text-blue-500"></i>
            Programación
          </h2>
        </header>
        <div class="px-5 py-4">
          <div class="space-y-2 mb-3">
            <label class="flex cursor-pointer items-center gap-2">
              <input type="radio" name="scheduling" [checked]="!form.scheduledAt" (change)="form.scheduledAt = ''" [disabled]="isReadOnly()"
                     class="h-4 w-4 text-blue-600" />
              <span class="text-sm text-gray-700 dark:text-gray-300">Enviar ahora / guardar borrador</span>
            </label>
            <label class="flex cursor-pointer items-center gap-2">
              <input type="radio" name="scheduling" [checked]="!!form.scheduledAt" (change)="form.scheduledAt = form.scheduledAt || ''" [disabled]="isReadOnly()"
                     class="h-4 w-4 text-blue-600" />
              <span class="text-sm text-gray-700 dark:text-gray-300">Programar para más tarde</span>
            </label>
          </div>
          @if (!!form.scheduledAt || form.scheduledAt === '') {
            <input type="datetime-local" [(ngModel)]="form.scheduledAt" [disabled]="isReadOnly()"
                   class="w-full rounded-full border px-4 py-2 text-sm text-gray-900 dark:text-white"
                   [class.border-gray-300]="!isScheduledDateInPast"
                   [class.dark:border-gray-700]="!isScheduledDateInPast"
                   [class.bg-white]="!isScheduledDateInPast"
                   [class.dark:bg-gray-800]="!isScheduledDateInPast"
                   [class.border-error-400]="isScheduledDateInPast"
                   [class.dark:border-error-500]="isScheduledDateInPast"
                   [class.bg-error-50]="isScheduledDateInPast"
                   [class.dark:bg-error-500/10]="isScheduledDateInPast" />
            @if (isScheduledDateInPast) {
              <p class="mt-1 text-xs text-error-600 dark:text-error-400 flex items-center gap-1.5">
                <i class="fas fa-triangle-exclamation"></i>
                Esa fecha y hora ya pasaron. Elige un momento posterior al actual.
              </p>
            } @else {
              <p class="mt-1 text-xs text-gray-400">Hora local del navegador. Se convierte a UTC al guardar.</p>
            }
          }
        </div>
      </section>

      <!-- Canal -->
      <section class="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-visible">
        <header class="flex items-center gap-2 border-b border-gray-100 dark:border-gray-800 px-5 py-3.5">
          <h2 class="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white/90">
            <i class="fas fa-paper-plane text-blue-500"></i>
            Canal de envío
          </h2>
        </header>
        <div class="px-5 py-4 space-y-2">
          <label class="flex cursor-pointer items-start gap-2 rounded-xl border p-3 transition-colors"
                 [class.border-blue-300]="useBulkDefault"
                 [class.bg-blue-50]="useBulkDefault"
                 [class.dark:bg-blue-900/20]="useBulkDefault"
                 [class.dark:border-blue-700]="useBulkDefault"
                 [class.border-gray-200]="!useBulkDefault"
                 [class.dark:border-gray-700]="!useBulkDefault">
            <input type="radio" [checked]="useBulkDefault" (change)="selectChannel(null)" [disabled]="isReadOnly()" class="mt-0.5" />
            <div class="flex-1">
              <p class="text-sm font-medium text-gray-900 dark:text-white/90">Mi canal bulk</p>
              <p class="text-xs text-gray-500 dark:text-gray-400">Configurado en la pantalla principal</p>
            </div>
          </label>

          <label class="flex cursor-pointer items-start gap-2 rounded-xl border p-3 transition-colors"
                 [class.border-blue-300]="!useBulkDefault"
                 [class.bg-blue-50]="!useBulkDefault"
                 [class.dark:bg-blue-900/20]="!useBulkDefault"
                 [class.dark:border-blue-700]="!useBulkDefault"
                 [class.border-gray-200]="useBulkDefault"
                 [class.dark:border-gray-700]="useBulkDefault">
            <input type="radio" [checked]="!useBulkDefault" (change)="onSelectOverride()" [disabled]="isReadOnly()" class="mt-0.5" />
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-gray-900 dark:text-white/90">Override de canal</p>
              @if (!useBulkDefault) {
                <!-- Selector de canal -->
                <div class="relative mt-2">
                  <button type="button" (click)="toggleChannelMenu()" [disabled]="isReadOnly()"
                          class="flex w-full items-center justify-between gap-2 rounded-full border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                    <span>
                      @if (activeChannel) {
                        {{ activeChannel.displayName }}
                        <span class="text-xs text-gray-500">· {{ channelLabel(activeChannel.channelType) }}</span>
                      } @else {
                        Selecciona un canal
                      }
                    </span>
                    <svg class="h-4 w-4 text-gray-400" [class.rotate-180]="channelMenuOpen" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                  </button>
                  @if (channelMenuOpen) {
                    <div class="absolute left-0 right-0 top-full z-30 mt-1 max-h-56 overflow-y-auto rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-xl">
                      @for (c of channels; track c.channelType + '_' + c.channelRefId) {
                        <button type="button" (click)="selectChannel(c)" [disabled]="!c.isConnected"
                                class="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50">
                          <div>
                            <p class="text-gray-900 dark:text-white/90">{{ c.displayName }}</p>
                            <p class="text-xs text-gray-500 dark:text-gray-400">{{ channelLabel(c.channelType) }} · {{ c.dailyRemaining }}/{{ c.dailyLimit }}</p>
                          </div>
                          @if (c.isPrimary) {
                            <span class="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">primario</span>
                          }
                        </button>
                      }
                    </div>
                  }
                </div>

                <!-- Destinatarios directos: email input con autocomplete -->
                <div class="mt-3 rounded-xl border border-blue-100 dark:border-blue-900/40 bg-blue-50/40 dark:bg-blue-900/10 p-3 space-y-2">
                  <p class="text-[0.68rem] font-semibold uppercase tracking-wide text-blue-700 dark:text-blue-300">
                    Destinatarios directos
                  </p>
                  <p class="text-[11px] text-blue-600 dark:text-blue-300/80">
                    Escribe cualquier correo válido y presiona Enter para agregarlo, esté o no registrado como contacto. Si coincide con un contacto, el autocompletado te lo sugerirá. Se suman a los segmentos seleccionados.
                  </p>

                  @if (!isReadOnly()) {
                    <!-- Input con autocomplete -->
                    <div class="relative">
                      <div class="flex items-center gap-2 rounded-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-1.5">
                        @if (overrideEmailsLoading) {
                          <svg class="h-3.5 w-3.5 flex-shrink-0 animate-spin text-gray-400" fill="none" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                          </svg>
                        } @else {
                          <svg class="h-3.5 w-3.5 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-4.35-4.35M17 11A6 6 0 105 11a6 6 0 0012 0z"/>
                          </svg>
                        }
                        <input
                          type="text"
                          [(ngModel)]="overrideEmailInput"
                          (ngModelChange)="onOverrideEmailInput()"
                          (keydown.enter)="$event.preventDefault(); addOverrideEmail()"
                          (blur)="closeOverrideSuggestions()"
                          placeholder="nombre@dominio.com o nombre del contacto…"
                          class="flex-1 bg-transparent text-xs text-gray-800 dark:text-white placeholder:text-gray-400 focus:outline-none"
                        />
                        @if (overrideEmailInput) {
                          <button type="button" (click)="overrideEmailInput = ''; overrideEmailSuggestionsOpen = false"
                                  class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                            <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                            </svg>
                          </button>
                        }
                        <button type="button" (click)="addOverrideEmail()" [disabled]="!canAddOverrideEmail"
                                title="Agregar este correo como destinatario directo"
                                class="flex-shrink-0 rounded-full bg-blue-600 px-2.5 py-0.5 text-[11px] font-semibold text-white hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                          Agregar
                        </button>
                      </div>

                      <!-- Sugerencias -->
                      @if (overrideEmailSuggestionsOpen && overrideEmailSuggestions.length > 0) {
                        <div class="absolute left-0 right-0 top-full z-30 mt-1 max-h-52 overflow-y-auto rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-xl">
                          @for (c of overrideEmailSuggestions; track c.id) {
                            <button type="button" (click)="selectOverrideContact(c)"
                                    class="flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-blue-50 dark:hover:bg-blue-900/20">
                              <!-- Avatar inicial -->
                              <span class="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/40 text-xs font-semibold text-blue-700 dark:text-blue-300">
                                {{ (c.fullname || '?')[0].toUpperCase() }}
                              </span>
                              <div class="min-w-0 flex-1">
                                <p class="text-sm font-medium text-gray-900 dark:text-white/90 truncate">{{ c.fullname }}</p>
                                <p class="text-xs text-gray-500 dark:text-gray-400 truncate">{{ c.mail }}</p>
                              </div>
                              <svg class="h-3.5 w-3.5 flex-shrink-0 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
                              </svg>
                            </button>
                          }
                        </div>
                      }

                      <!-- Sin resultados -->
                      @if (overrideEmailInput.length >= 2 && !overrideEmailsLoading && overrideEmailSuggestions.length === 0 && overrideEmailSuggestionsOpen === false) {
                        @if (canAddOverrideEmail) {
                          <p class="mt-1 text-[11px] text-blue-600 dark:text-blue-300 px-1">
                            Ningún contacto coincide. Presiona <strong>Enter</strong> o «Agregar» para sumar este correo directamente.
                          </p>
                        } @else {
                          <p class="mt-1 text-[11px] text-gray-500 dark:text-gray-400 px-1">
                            Escribe un correo con formato válido para agregarlo, o el nombre de un contacto para buscarlo.
                          </p>
                        }
                      }
                    </div>
                  }

                  <!-- Lista de contactos ya agregados como directos -->
                  @if (manualRecipients.length > 0) {
                    <div class="mt-2 flex flex-wrap gap-1.5">
                      @for (r of manualRecipients; track r.id) {
                        <span class="inline-flex items-center gap-1.5 rounded-full bg-white dark:bg-gray-800 border border-blue-200 dark:border-blue-700 px-2.5 py-1 text-xs text-gray-800 dark:text-white">
                          <span class="max-w-[120px] truncate">{{ r.fullName || r.email }}</span>
                          @if (!isReadOnly()) {
                            <button type="button" (click)="removeManualRecipient(r)"
                                    class="text-gray-400 hover:text-red-500 transition-colors">
                              <svg class="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                              </svg>
                            </button>
                          }
                        </span>
                      }
                    </div>
                  }
                  @if (manualRecipients.length === 0 && isReadOnly()) {
                    <p class="text-[11px] text-gray-400">Sin destinatarios directos configurados.</p>
                  }
                </div>
              }
            </div>
          </label>
        </div>
      </section>

      <!-- ── Adjuntos (gate: solo si el canal bulk efectivo los soporta) ── -->
      @if (adjuntosSoportados === true) {
        <section class="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-visible">
          <header class="flex items-center gap-2 border-b border-gray-100 dark:border-gray-800 px-5 py-3.5">
            <h2 class="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white/90">
              <i class="fas fa-paperclip text-blue-500"></i>
              Adjuntos
            </h2>
          </header>
          <div class="px-5 py-4 space-y-3">
            @if (!isReadOnly()) {
              <input #campaignFileInput type="file" multiple hidden (change)="onSeleccionarArchivos($event)" />
              <button type="button" (click)="campaignFileInput.click()" [disabled]="saving"
                      class="inline-flex items-center gap-2 rounded-full border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-2 text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-60">
                <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"></path>
                </svg>
                Adjuntar archivos
              </button>
              <p class="text-[11px] text-gray-500 dark:text-gray-400">Máximo 10 MB por archivo. Se enviarán junto al correo a todos los destinatarios.</p>
            }

            @if (attachments.length > 0) {
              <div class="flex flex-wrap gap-2">
                @for (att of attachments; track att.key) {
                  <div class="flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-xs"
                       [ngClass]="att.error
                         ? 'border-red-200 bg-red-50 dark:border-red-500/30 dark:bg-red-500/10'
                         : 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800'">
                    @if (att.uploading) {
                      <span class="h-3.5 w-3.5 animate-spin rounded-full border-b-2 border-blue-500"></span>
                    } @else if (att.error) {
                      <svg class="h-3.5 w-3.5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01M5.07 19h13.86a2 2 0 001.74-3L13.74 4a2 2 0 00-3.48 0L3.33 16a2 2 0 001.74 3z"></path>
                      </svg>
                    } @else {
                      <svg class="h-3.5 w-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"></path>
                      </svg>
                    }
                    <div class="min-w-0">
                      <p class="max-w-[140px] truncate font-medium"
                         [ngClass]="att.error ? 'text-red-600 dark:text-red-400' : 'text-gray-700 dark:text-gray-300'">{{ att.name }}</p>
                      <p class="text-[10px] text-gray-400">
                        @if (att.error) { Error } @else if (att.uploading) { Subiendo… } @else { {{ formatSize(att.size) }} }
                      </p>
                    </div>
                    @if (!isReadOnly()) {
                      <button type="button" (click)="quitarAdjunto(att)" title="Quitar"
                              class="shrink-0 rounded p-0.5 text-gray-400 hover:bg-gray-200 hover:text-gray-600 dark:hover:bg-gray-700">
                        <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                        </svg>
                      </button>
                    }
                  </div>
                }
              </div>
            }
          </div>
        </section>
      } @else if (adjuntosSoportados === false) {
        <!-- Gate: el canal efectivo no soporta adjuntos → nota explicativa -->
        <section class="rounded-2xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-5 py-3.5">
          <div class="flex items-start gap-2.5">
            <i class="fas fa-paperclip mt-0.5 text-amber-500"></i>
            <div class="min-w-0">
              <p class="text-xs font-semibold text-amber-900 dark:text-amber-100">Adjuntos no disponibles</p>
              <p class="mt-0.5 text-[11px] text-amber-800 dark:text-amber-200">
                El canal de envío de correos actual{{ gateEffectiveChannelType ? ' (' + channelLabel(gateEffectiveChannelType) + ')' : '' }} no admite adjuntos.
                Conecta o selecciona Gmail, Outlook o SMTP como canal para poder adjuntar archivos.
              </p>
            </div>
          </div>
        </section>
      }

      <!-- Cumplimiento legal -->
      <section class="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-5 py-3 text-xs text-gray-500 dark:text-gray-400">
        <i class="fas fa-gavel text-blue-500"></i> Cumplimiento legal activo (GDPR/CAN-SPAM)
      </section>
    </div>

    <!-- ── COLUMNA DERECHA: editor del correo ── -->
    <div class="w-full lg:flex-[2] lg:min-w-0">
      <section class="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
        <header class="flex items-center gap-2 border-b border-gray-100 dark:border-gray-800 px-5 py-3.5">
          <h2 class="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white/90">
            <i class="fas fa-pen-ruler text-blue-500"></i>
            Editor de email
          </h2>
        </header>
        <div class="px-5 py-4 space-y-4">

          <!-- Nombre interno -->
          <div>
            <label class="block text-[0.7rem] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5">Nombre del correo *</label>
            <input type="text" [(ngModel)]="form.name" maxlength="200" [disabled]="isReadOnly()"
                   placeholder="Promo Q2 2026"
                   class="w-full rounded-full border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-2 text-sm text-gray-900 dark:text-white" />
          </div>

          <!-- Asunto + preheader -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label class="block text-[0.7rem] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5">Asunto *</label>
              <input type="text" [(ngModel)]="form.subject" maxlength="998" [disabled]="isReadOnly()"
                     placeholder="Aprovecha 30% en abril"
                     class="w-full rounded-full border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-2 text-sm text-gray-900 dark:text-white" />
            </div>
            <div>
              <label class="block text-[0.7rem] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5">Preencabezado</label>
              <input type="text" [(ngModel)]="form.preheader" maxlength="255" [disabled]="isReadOnly()"
                     placeholder="Solo esta semana"
                     class="w-full rounded-full border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-2 text-sm text-gray-900 dark:text-white" />
            </div>
          </div>

          <!-- Cuerpo del correo: visualización + acciones para elegir o crear plantilla -->
          <div>
            <div class="mb-2 flex items-center justify-between gap-2 flex-wrap">
              <label class="text-[0.7rem] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Cuerpo del correo *</label>
              <div class="flex items-center gap-2 flex-wrap">
                @if (!isReadOnly()) {
                  <button type="button"
                          (click)="openStarterModal()"
                          class="inline-flex items-center gap-1.5 rounded-full bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 text-xs font-medium">
                    <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                    </svg>
                    Comenzar desde plantilla
                  </button>
                  <button type="button" (click)="openTemplatePicker()"
                          class="inline-flex items-center gap-1.5 rounded-full border border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20 px-3 py-1 text-xs font-medium text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/40">
                    <i class="fas fa-file-import"></i>
                    {{ selectedTemplate ? 'Cambiar plantilla' : 'Usar plantilla guardada' }}
                  </button>
                  <button type="button" (click)="goEditContent()" [disabled]="saving"
                          [title]="isEditing
                            ? 'Abre el editor visual de bloques (columnas, hero, CTA, etc.)'
                            : 'Guarda el correo como borrador y luego abre el editor de bloques'"
                          class="inline-flex items-center gap-1.5 rounded-full border border-gray-300 dark:border-gray-700 px-3 py-1 text-xs font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-60">
                    <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h7"></path>
                    </svg>
                    Editor visual avanzado
                    <svg class="h-3 w-3 opacity-60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                    </svg>
                  </button>
                }
              </div>
            </div>

            <p class="mb-2 text-[11px] text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
              <i class="fas fa-info-circle"></i>
              Escribí el contenido directamente abajo. Para layouts con columnas, hero o botones CTA usá el "Editor visual avanzado".
            </p>

            <!-- Plantilla aplicada (chip) -->
            @if (selectedTemplate) {
              <div class="mb-2 flex items-center justify-between gap-2 rounded-xl border border-blue-200 dark:border-blue-700 bg-blue-50/60 dark:bg-blue-900/15 px-3 py-2">
                <div class="min-w-0 flex-1 flex items-center gap-2">
                  <i class="fas fa-file-lines text-blue-500"></i>
                  <div class="min-w-0">
                    <p class="text-xs font-medium text-blue-900 dark:text-blue-200 truncate">
                      Plantilla: {{ selectedTemplate.name }}
                    </p>
                    <p class="text-[10px] text-blue-600 dark:text-blue-300/80">
                      Snapshot al cuerpo del correo. Editar la plantilla después no afecta este correo.
                    </p>
                  </div>
                </div>
                @if (!isReadOnly()) {
                  <button type="button" (click)="removeTemplate()"
                          class="text-xs text-blue-700 dark:text-blue-300 hover:underline">
                    Quitar
                  </button>
                }
              </div>
            }

            <!-- Warnings de variables huérfanas -->
            @if (templateWarnings.length > 0) {
              <div class="mb-2 rounded-xl border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 px-3 py-2">
                @for (w of templateWarnings; track w) {
                  <p class="text-xs text-amber-800 dark:text-amber-200 flex items-start gap-1.5">
                    <i class="fas fa-triangle-exclamation mt-0.5"></i>
                    <span>{{ w }}</span>
                  </p>
                }
              </div>
            }

            <!-- Toggle de ancho simulado (desktop/mobile) -->
            <div class="mb-2 flex justify-end">
              <div class="inline-flex rounded-full border border-gray-200 dark:border-gray-700 p-0.5 text-[10px]">
                <button type="button" (click)="previewMode = 'desktop'"
                        [class.bg-gray-900]="previewMode === 'desktop'"
                        [class.text-white]="previewMode === 'desktop'"
                        [class.dark:bg-white]="previewMode === 'desktop'"
                        [class.dark:text-gray-900]="previewMode === 'desktop'"
                        [class.text-gray-500]="previewMode !== 'desktop'"
                        class="rounded-full px-2 py-0.5 font-medium" title="Desktop">💻</button>
                <button type="button" (click)="previewMode = 'mobile'"
                        [class.bg-gray-900]="previewMode === 'mobile'"
                        [class.text-white]="previewMode === 'mobile'"
                        [class.dark:bg-white]="previewMode === 'mobile'"
                        [class.dark:text-gray-900]="previewMode === 'mobile'"
                        [class.text-gray-500]="previewMode !== 'mobile'"
                        class="rounded-full px-2 py-0.5 font-medium" title="Mobile">📱</button>
              </div>
            </div>

            <!-- Edición nativa, inline, sin navegar a otra pantalla. -->
            <div class="bg-gray-100 dark:bg-gray-800 rounded-2xl p-4 flex justify-center">
              <div class="w-full transition-all" [style.max-width.px]="previewMode === 'mobile' ? 360 : 720">
                <app-rich-text-editor
                  [value]="form.bodyHtml"
                  (valueChange)="form.bodyHtml = $event"
                  [disabled]="isReadOnly()"
                  [variableNamespaces]="availableNamespaces"
                  [uploadHandler]="bodyImageUploadHandler"
                  [attachmentUploadHandler]="bodyAttachmentUploadHandler"
                  placeholder="Escribe el contenido de tu correo..." />
              </div>
            </div>

            @if (form.subject) {
              <div class="mt-2 rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-4 py-2 text-xs">
                <p class="text-gray-400 uppercase tracking-wide mb-0.5">Cómo se ve en la bandeja</p>
                <p class="text-gray-900 dark:text-white/90 truncate">{{ form.subject }}</p>
                @if (form.preheader) {
                  <p class="text-gray-500 dark:text-gray-400 truncate">{{ form.preheader }}</p>
                }
              </div>
            }
          </div>

          <!-- Acciones secundarias para mobile (donde no hay sticky header) -->
          @if (!isReadOnly()) {
            <div class="flex flex-wrap gap-2 border-t border-gray-100 dark:border-gray-800 pt-4 md:hidden">
              <button *appHasPermission="'campaigns:edit'" type="button" (click)="saveDraft()" [disabled]="guardarDeshabilitado"
                      class="flex-1 rounded-full border border-gray-300 dark:border-gray-700 px-4 py-2 text-xs font-medium text-gray-700 dark:text-gray-300 disabled:opacity-60">
                Guardar borrador
              </button>
              <!-- Inc 42: misma jerarquía invertida que el header sticky. -->
              <button *appHasPermission="'campaigns:edit'" type="button" (click)="schedule()" [disabled]="guardarDeshabilitado || !form.scheduledAt || isScheduledDateInPast"
                      class="flex-1 rounded-full px-4 py-2 text-xs disabled:opacity-60"
                      [class.bg-brand-500]="hasFutureSchedule"
                      [class.text-white]="hasFutureSchedule"
                      [class.font-semibold]="hasFutureSchedule"
                      [class.border]="!hasFutureSchedule"
                      [class.border-blue-300]="!hasFutureSchedule"
                      [class.dark:border-blue-700]="!hasFutureSchedule"
                      [class.bg-blue-50]="!hasFutureSchedule"
                      [class.dark:bg-blue-900\/20]="!hasFutureSchedule"
                      [class.text-blue-700]="!hasFutureSchedule"
                      [class.dark:text-blue-300]="!hasFutureSchedule"
                      [class.font-medium]="!hasFutureSchedule">
                Programar
              </button>
              <button *appHasPermission="'campaigns:edit'" type="button" (click)="launchNow()"
                      [disabled]="guardarDeshabilitado || hasFutureSchedule"
                      [title]="lanzarAhoraTitle"
                      class="flex-1 rounded-full px-4 py-2 text-xs disabled:opacity-60 disabled:cursor-not-allowed"
                      [class.bg-gray-900]="!hasFutureSchedule"
                      [class.dark:bg-white]="!hasFutureSchedule"
                      [class.text-white]="!hasFutureSchedule"
                      [class.dark:text-gray-900]="!hasFutureSchedule"
                      [class.font-semibold]="!hasFutureSchedule"
                      [class.border]="hasFutureSchedule"
                      [class.border-gray-300]="hasFutureSchedule"
                      [class.dark:border-gray-700]="hasFutureSchedule"
                      [class.text-gray-700]="hasFutureSchedule"
                      [class.dark:text-gray-300]="hasFutureSchedule"
                      [class.font-medium]="hasFutureSchedule">
                Lanzar
              </button>
            </div>
          }
        </div>
      </section>
    </div>

  </div>
}
</div>
</div>

<!-- Modal selector de plantilla -->
<app-template-picker-modal
  [isOpen]="showTemplatePicker"
  (selected)="onTemplateSelected($event)"
  (cancelled)="closeTemplatePicker()"
></app-template-picker-modal>

<!-- Modal selector de contactos (incluir / excluir) -->
<app-contact-picker-modal
  [isOpen]="contactPickerOpen"
  [mode]="contactPickerMode"
  [excludeContactIds]="pickerAlreadyAttachedIds"
  (confirmed)="onContactPickerConfirm($event)"
  (cancelled)="closeContactPicker()"
></app-contact-picker-modal>

<!-- Modal de plantillas iniciales (Bienvenida, Newsletter, etc.) -->
<app-starter-template-modal
  [isOpen]="starterModalOpen"
  (selected)="onStarterPicked($event)"
  (cancelled)="starterModalOpen = false"
></app-starter-template-modal>

<!-- Modal: contactos de un segmento (búsqueda + scroll infinito) -->
<app-segment-members-modal
  [isOpen]="segmentMembersModalOpen"
  [segmentId]="membersModalSegmentId"
  [segmentName]="membersModalSegmentName"
  (closed)="closeSegmentMembers()"
></app-segment-members-modal>
